// Wire protocol for the client <-> server WebSocket.
// Every message is JSON for now; we can swap to a binary encoding later
// without changing call sites by keeping everything funneled through these
// discriminated unions.
//
// CROSS-CLIENT CONTRACT: this file is mirrored to C# in the Unity desktop
// client at `desktop/Assets/Scripts/Protocol/`. Both the web client and the
// Unity desktop client send/receive these messages against the same
// authoritative server — so a web player and a Unity player end up in the
// same lobby and same match. If you change a message shape here, mirror it
// in the C# DTOs in the same PR, bump PROTOCOL_VERSION, and coordinate the
// client release. Changes that are purely server-side (physics tweaks, AI
// tuning, scoring rules) do NOT require a client update — both clients
// just receive the new snapshots.

import type { Theme } from "./theme.js";

// v7: snapshot slimming. WorldSnapshot.players narrowed to the per-tick
// PlayerPose (id/x/y/dir/angle/alive/onBoat/sightLen); the slow-changing
// identity/inventory fields moved to PlayerInfo, shipped delta-on-change in
// the new optional WorldSnapshot.playersInfo (absent = unchanged, same
// contract as pillboxes/bases/mines since v4). Clients merge info back into
// the pose objects before handing snapshots to consumers, so everything
// downstream still sees the full PlayerState.
// v6: skins + achievements (all additive/optional — v5 clients are
// unaffected). ClientMessage gained "setSkin"; PlayerState and
// WarmupRosterEntry gained optional `skin` (omitted when default);
// ServerMessage gained the single-recipient "achievement" unlock toast.
// v5: MatchPhase gained "warmup"; MatchState gained warmupEndsAt + warmupRoster
// (for the new pre-match Match Lobby on matchmade rooms). PlayerState gained
// optional colorIndex (FFA tank colour picker). ClientMessage gained "setColor".
// v4: WorldSnapshot.mines/pillboxes/bases became optional — the server now
// only includes a slow-changing collection on ticks where it actually changed
// (delta-on-change). An absent field means "unchanged since the last snapshot";
// clients retain their last-known list. Players/shells/builders/match are still
// sent every tick. Newly-joined clients always receive a full snapshot first.
export const PROTOCOL_VERSION = 7;

// ---------- Client -> Server ----------

export type BuilderAction =
  | "auto"      // server picks the right action from the target tile
  | "wall"      // build wall on grass (-2 trees) - LGM_BUILDING_REQUEST
  | "road"      // build road on grass (-2 trees) - LGM_ROAD_REQUEST
  | "tree"      // harvest forest tile (+4 trees) - LGM_TREE_REQUEST
  | "boat"      // build boat on river (-20 trees) - LGM_BOAT_REQUEST
  | "repair"    // repair a damaged friendly pillbox (1-4 trees, full heal) - LGM_PILL_REQUEST
  | "pill"      // deploy a carried pillbox (-4 trees) - LGM_PILL_REQUEST (new placement)
  | "mine";     // lay a mine on a drivable tile (-1 mine) - LGM_MINE_REQUEST

export type ClientMessage =
  | { t: "hello"; version: number }
  | { t: "input"; seq: number; throttle: number; turn: number; fire: boolean; laying: boolean; sightUp: boolean; sightDown: boolean }
  | { t: "builder"; action: BuilderAction; tileX: number; tileY: number }
  // action "pickup" ignores tileX/tileY. action "drop" uses them when
  // provided (mouse-click placement); otherwise the server falls back to
  // the tile directly in front of the tank (keyboard P shortcut).
  | { t: "pillbox"; action: "pickup" | "drop"; tileX?: number; tileY?: number }
  | { t: "chat"; text: string }
  // Pick a tank-tint colour (FFA only — ignored in team rooms where the team
  // colour wins). Server clamps colorIndex against TEAM_COLORS.length and
  // accepts it during warmup or playing phases.
  | { t: "setColor"; colorIndex: number }
  // Equip a tank skin (see skins.ts). Server validates the id against the
  // caller's entitlements (DB user_skins); "default" is always allowed.
  // Unlike setColor this works in every room type — skins are personal
  // cosmetics, valid in team rooms too.
  | { t: "setSkin"; skinId: string }
  | { t: "ping"; clientTime: number };

// ---------- Server -> Client ----------

// Per-tick pose — everything that changes essentially every tick. This is
// what WorldSnapshot.players carries on the wire since v7.
export interface PlayerPose {
  id: number;
  x: number;
  y: number;
  dir: number;
  angle: number; // continuous 0..256 bradians for smooth gunsight placement
  alive: boolean;
  onBoat: boolean; // true at spawn while crossing water; false once disembarked
  sightLen: number;  // gunsight range in half-tiles [GUNSIGHT_MIN..GUNSIGHT_MAX]
}

// Slow-changing identity/inventory — shipped delta-on-change in
// WorldSnapshot.playersInfo since v7 (absent = unchanged; clients keep and
// re-apply their last-known entries, keyed by id).
export interface PlayerInfo {
  id: number;
  name: string;
  team: number;   // 0 = free-for-all
  kills: number;
  deaths: number;
  armor: number;
  shells: number;
  mines: number;
  trees: number;
  pillboxes: number; // carried pillboxes
  // FFA tank-tint index into TEAM_COLORS. Optional; absent = default tank
  // colour. Only honoured by clients in FFA rooms — in team rooms the
  // renderer uses the team colour regardless.
  colorIndex?: number;
  // Equipped tank skin id (see skins.ts). Omitted when the default skin is
  // equipped, so pre-v6 snapshots are byte-identical for unskinned players.
  skin?: string;
}

// The merged view every consumer works with: each client's merge layer
// copies the cached PlayerInfo fields onto the pose objects (in place)
// before handing the snapshot on, so nothing downstream sees the wire
// split. Server-side gameplay code reads world.tanks, not snapshots.
export interface PlayerState extends PlayerPose, PlayerInfo {}

export interface ShellState {
  id: number;
  x: number;
  y: number;
  angle: number; // continuous 0..256 bradians; sprite = floor(angle/16) % 16
  ownerId: number;
}

export interface PillboxState {
  id: number;
  tileX: number;
  tileY: number;
  owner: number;    // 0 = neutral; otherwise playerId of last owner
  team: number;     // 0 = neutral / FFA-owned, else team slot
  armor: number;
  dropped: boolean; // armor hit 0; can be picked up by any tank standing on it
}

export interface BaseState {
  id: number;
  tileX: number;
  tileY: number;
  owner: number;
  team: number;
  armor: number;
  shells: number;
  mines: number;
}

export interface MineState {
  id: number;
  tileX: number;
  tileY: number;
  ownerId: number;
}

export interface BuilderState {
  playerId: number;   // tank this builder belongs to
  x: number;
  y: number;
  alive: boolean;
  busy: boolean;
  // True while the builder is being helicoptered in from a random map start
  // to the death-time tank position. Clients render the helicopter sprite /
  // model on top of the LGM during this phase and a regular running figure
  // once it flips back to false. Mirrors lgm.c's isDead+LGM_HELICOPTER_FRAME
  // composite state (a dead LGM IS the helicopter frame for the parachute).
  parachuting?: boolean;
  // Drop-off point in world pixels, present only while parachuting. The
  // desktop helicopter view uses this to keep its spotlights aimed at where
  // the LGM is being delivered (frozen at the moment of death — does not
  // chase a moving tank).
  destX?: number;
  destY?: number;
}

export type MatchPhase = "warmup" | "playing" | "ended";

// Match Lobby roster entry — present on MatchState.warmupRoster during the
// pre-match countdown so clients can show who's queued up without needing the
// (filtered) per-tank snapshot.
export interface WarmupRosterEntry {
  playerId: number;
  username: string;
  // FFA tank-tint index the player picked in the Match Lobby. Absent =
  // hasn't picked. Empty/0 is a valid pick (the first swatch).
  colorIndex?: number;
  // Equipped tank skin id; omitted when default. Lets the lobby roster show
  // a skin glyph next to each name.
  skin?: string;
}

export interface MatchState {
  phase: MatchPhase;
  startedAt: number;      // ms epoch
  endsAt: number;         // ms epoch; only meaningful during "playing"
  restartAt: number;      // ms epoch; only meaningful during "ended"
  winnerId: number | null;// player id, or null for a draw (tie or empty)
  // Only meaningful during "warmup": ms epoch the countdown ends and the
  // match goes live. 0 outside warmup.
  warmupEndsAt?: number;
  // Only present during "warmup": the list of humans currently in the
  // Match Lobby so each client can render the roster without leaking
  // other tanks' positions.
  warmupRoster?: WarmupRosterEntry[];
}

// SIEGE wave-survival state, present only in siege rooms. Drives the big
// top-centre countdown/round HUD on the client.
export type SiegePhase = "prep" | "wave" | "break" | "gameover";
export interface SiegeState {
  phase: SiegePhase;
  // Current wave number. 0 during the opening prep window (round 1 incoming).
  round: number;
  // ms epoch the current timed phase ends (prep / break / gameover). 0 during
  // an active wave (which ends when all enemies are cleared, not on a timer).
  phaseEndsAt: number;
  // Live enemy tanks remaining in the current wave.
  enemiesAlive: number;
  // Highest round reached in this room so far (for the HUD / scoreboard).
  bestRound: number;
}

// BATTLE ROYALE shrinking-zone state, present only in battle-royale rooms.
// Server-authoritative: the client renders the dense fog outside the safe
// circle and the zone HUD straight from these fields. All coordinates are in
// world pixels (same scale as PlayerState.x/y).
export interface BattleRoyaleState {
  // "waiting" while the Match Lobby warmup is still running (no fog yet),
  // "shrinking" during the live match, "gameover" once a winner is decided.
  phase: "waiting" | "shrinking" | "gameover";
  // 1-based fog stage (see BR_STAGES); 0 while waiting.
  stage: number;
  stageCount: number;
  // Centre of the (fixed) final safe zone the fog closes toward.
  zoneX: number;
  zoneY: number;
  // Current safe radius and the radius the fog is shrinking toward by the end
  // of the active stage — both in world pixels.
  zoneRadius: number;
  targetRadius: number;
  // ms epoch the current stage ends (for the HUD countdown).
  stageEndsAt: number;
  // Armour-points-per-second drained from a tank caught outside the circle.
  drainPerSec: number;
  // Tanks still alive (humans + bots) — the "N left" headcount.
  alive: number;
}

export interface WorldSnapshot {
  tick: number;
  // Wire: PlayerPose[] every tick. After a client's merge layer runs, the
  // same objects also carry the PlayerInfo fields — consumers read them as
  // PlayerState[] (see PlayerState).
  players: PlayerPose[];
  // Slow-changing player identity/inventory, delta-on-change (v7): present =
  // authoritative full list this tick; absent = unchanged since the last
  // snapshot that carried it.
  playersInfo?: PlayerInfo[];
  shells: ShellState[];
  builders: BuilderState[];
  match: MatchState;
  // Present only in siege rooms; absent for classic/turbo.
  siege?: SiegeState;
  // Present only in battle-royale rooms; absent otherwise.
  battleRoyale?: BattleRoyaleState;
  // Slow-changing collections. Present = authoritative full list for that
  // collection on this tick; absent (undefined) = unchanged since the last
  // snapshot, so the client should keep its last-known list. See
  // PROTOCOL_VERSION note above.
  pillboxes?: PillboxState[];
  bases?: BaseState[];
  mines?: MineState[];
}

// A snapshot after a client's merge layer has re-applied the cached
// playersInfo onto the pose objects: `players` entries are full
// PlayerState. This is what snapshot consumers (renderers, HUDs, bots)
// receive; WorldSnapshot is the wire shape.
export interface MergedWorldSnapshot extends Omit<WorldSnapshot, "players"> {
  players: PlayerState[];
}

export interface TileUpdate {
  x: number;
  y: number;
  terrain: number;
}

// Wire-friendly map description sent to clients on join. Terrain is sent as
// a base64-encoded Uint8Array to keep JSON payloads compact.
export interface MapDescriptor {
  name: string;
  author: string;
  width: number;
  height: number;
  terrainBase64: string; // base64 of row-major width*height Uint8 tile ids
  // Visual skin rolled by the server when the room was created; every
  // client in the room receives the same value. Pure visual; gameplay is
  // unaffected. Older payloads without this field decode as Theme.Default.
  theme: Theme;
}

export type ServerMessage =
  | { t: "welcome"; playerId: number; version: number }
  | { t: "error"; code: string; message: string }
  | { t: "gameJoined"; gameId: string; selfId: number; map: MapDescriptor }
  | { t: "snapshot"; snap: WorldSnapshot }
  | { t: "tiles"; updates: TileUpdate[] }
  | { t: "chat"; from: string; text: string; ts: number }
  // Single-recipient builder-feedback message ("Insufficient trees",
  // "Can't build there", etc.). Mirrors WinBolo's MESSAGE_ASSISTANT
  // (lgm.c).
  | { t: "assist"; text: string; ts: number }
  // Single-recipient achievement-unlock toast, sent the moment a threshold
  // is crossed mid-match. Clients without a handler ignore unknown types.
  | { t: "achievement"; id: string; name: string; rewardSkinId?: string }
  | { t: "pong"; clientTime: number; serverTime: number };

// ---------- Lobby / rooms ----------

// Per-room configuration the admin panel will eventually manage. Keep this
// shape flat and serializable so it round-trips through JSON without
// special cases.
// Sentinel mapId requesting a random map that re-rolls between matches.
// Stored as-is in RoomSettings; the server picks a concrete map per match
// and exposes the current one via MapDescriptor.
export const RANDOM_MAP_ID = "__random__";

// Matchmaker queue identities. "super_ffa" is the FFA queue with Turbo
// physics enabled (~30% faster tank movement, larger shell capacity, faster
// fire rate — see GameStyle in constants.ts). The UI labels it SUPER FREE
// FOR ALL but the queue-wire identifier stays super_ffa to mirror the
// snake_case of the original ffa/teams entries.
export type MatchmadeMode = "ffa" | "super_ffa" | "teams" | "battle_royale";

export interface RoomSettings {
  teamCount: number; // 0 = FFA-only, 2..MAX_TEAM = team mode with that many teams
  mapId: string;     // empty string = procedural starter isle, RANDOM_MAP_ID = re-roll each match
  // Number of AI-controlled tanks ("brains" in WinBolo terminology) the
  // room maintains. The room tops up to this count whenever it has open
  // slots and trims bots when humans fill the room. Capped at
  // MAX_PLAYERS_PER_GAME - 1 so a room can never be all bots.
  botCount: number;
  // CLASSIC = original WinBolo balance. TURBO = 2x speed/accel, 1.5x
  // armor & shell capacity, ~1.7x fire rate. See TankTuning in
  // constants.ts.
  style: import("./constants.js").GameStyle;
  // Set on rooms spawned by /api/matchmake. Matchmade rooms run a 15s
  // pre-match warmup (admin-configurable) the first time they boot, are
  // hidden from the public lobby list, defer bot population to the end of
  // warmup, and never re-enter warmup on subsequent map cycles. Absent =
  // user-created or preset room (today's behaviour).
  matchmade?: boolean;
  // Set alongside `matchmade`: which queue this room serves. Used by the
  // matchmaker to pick the fullest non-full room of the right shape.
  mode?: MatchmadeMode;
}

export interface MapInfo {
  id: string;
  name: string;
  author: string;
}
export interface MapListResponse {
  maps: MapInfo[];
}

export interface GameListEntry {
  id: string;
  name: string;
  players: number;
  maxPlayers: number;
  teamCount: number;
  // teamCounts[0] = players without a team (always 0 in team rooms).
  // teamCounts[i] = number of players on team i (1..teamCount).
  // In FFA rooms, teamCounts[0] = total players and the array length is 1.
  teamCounts: number[];
  mapName: string; // "" for the procedural starter isle
  botCount: number; // number of AI tanks the room is configured to maintain
  style: import("./constants.js").GameStyle; // "classic" | "turbo" | "siege"
  // True for the synthetic always-on SIEGE single-player challenge row. The
  // client renders it specially and, on join, creates a fresh private siege
  // room rather than connecting to this (non-existent) id.
  siegeChallenge?: boolean;
  // For the challenge row: how many players are "defending their own island"
  // right now (real solo-siege rooms + a small random pad so it's never zero).
  siegeDefenders?: number;
}
export interface GameListResponse {
  games: GameListEntry[];
}
export interface CreateGameRequest {
  name: string;
  teamCount?: number; // 0 (FFA) or 2..MAX_TEAM; defaults to DEFAULT_TEAM_COUNT
  mapId?: string;     // empty string or omitted = procedural starter isle
  botCount?: number;  // 0..MAX_PLAYERS_PER_GAME-1; defaults to 0
  style?: import("./constants.js").GameStyle; // defaults to "classic"
  // When true, create a hidden single-player siege room (random map+theme)
  // that never appears in the public lobby list and is torn down when its
  // one player leaves. Only meaningful with style "siege".
  solo?: boolean;
}
export interface CreateGameResponse {
  id: string;
  name: string;
  teamCount: number;
  mapId: string;
  botCount: number;
  style: import("./constants.js").GameStyle;
}

// ---------- REST DTOs ----------

export interface RegisterRequest {
  username: string;
  password: string;
  email: string;
}
export interface LoginRequest {
  username: string;
  password: string;
}
export interface AuthResponse {
  username: string;
}

// ---------- Steam auth ----------
// Seamless sign-in for the Steam desktop build. The Unity client fetches a
// Steam "web API" auth ticket via Steamworks, posts the hex-encoded ticket
// here, and the server verifies it against the Steam Web API. On first sign-in
// we provision a webolo account keyed by SteamID, copying the Steam persona
// name (username) and the supplied email into `users`.
export interface SteamLoginRequest {
  /** Hex-encoded session ticket from ISteamUser.GetAuthTicketForWebApi. */
  ticket: string;
  /**
   * Optional email to store on first provisioning. Steam does not expose the
   * account email to apps, so the client may collect one; if omitted the
   * server stores a synthetic placeholder it can replace later.
   */
  email?: string;
}

// Two-phase response. The happy path returns `AuthResponse` (cookie set, done).
// If the Steam persona name collides with an existing non-Steam account, the
// server returns 409 with this payload instead: the client prompts the player
// for a fresh username and replays it to /steam/complete with the pendingToken.
export interface SteamUsernameTakenResponse {
  error: "steam_username_taken";
  /** Short-lived signed token binding the verified SteamID for the follow-up. */
  pendingToken: string;
  /** A still-free suggestion the client can pre-fill (e.g. persona + digits). */
  suggested: string;
}

export interface SteamCompleteRequest {
  /** The pendingToken handed back by /steam when the persona name was taken. */
  pendingToken: string;
  /** Player-chosen username. Re-validated server-side; may still 409. */
  username: string;
  /** Optional email to store; synthetic placeholder used when omitted. */
  email?: string;
}
export interface MeResponse {
  username: string;
  gamesPlayed: number;
  kills: number;
  deaths: number;
  wins: number;
  losses: number;
  // Highest siege wave the player has ever reached. 0 if they've never played.
  siegeBestRound: number;
  // One-time "Full Game Unlock" entitlement. The web client ignores this (web
  // is always free); the native clients use it to ungate paid lobbies and skip
  // the paywall. Read from users.premium_at (server-side). Always re-fetch /me
  // after a purchase — the flag is never baked into the session JWT, so an
  // admin grant/revoke takes effect immediately.
  premium: boolean;
}

// ---------- Sign in with Apple ----------
// Mirrors the Steam two-phase flow. The native Apple builds (iOS/macOS/tvOS)
// obtain an identity token via Sign in with Apple, POST it to /api/auth/apple,
// and the server verifies it against Apple's public keys, then logs the player
// into their apple_sub-linked account or provisions a new one. The only prompt
// is a username collision, which routes through /apple/complete exactly like
// Steam.
export interface AppleLoginRequest {
  /** The identity token (a JWT) from ASAuthorizationAppleIDCredential. */
  identityToken: string;
  /**
   * Display name from Apple, delivered ONLY on the first authorization, so the
   * client must persist and forward it here on first sign-in. Used to pre-fill
   * the username; falls back to the email local-part when absent.
   */
  fullName?: string;
  /** Optional email to store on first provisioning (synthetic if omitted). */
  email?: string;
}

export interface AppleUsernameTakenResponse {
  error: "apple_username_taken";
  /** Short-lived signed token binding the verified Apple sub for the follow-up. */
  pendingToken: string;
  /** A still-free suggestion the client can pre-fill. */
  suggested: string;
}

export interface AppleCompleteRequest {
  pendingToken: string;
  username: string;
  email?: string;
}

// ---------- In-app purchase / entitlement ----------

// The single one-time product. Stable store-registered identifier — append-only,
// never rename (it's the App Store / Play product id and a wire contract).
export const FULL_GAME_PRODUCT_ID = "full_game_unlock";
// The entitlement key stored in user_entitlements.
export const FULL_GAME_ENTITLEMENT = "full_game";

export type StorePlatform = "apple" | "steam" | "google";

// Localized price the native paywall shows, fetched from the platform store on
// the client (typed here so the paywall DTO is consistent across clients).
export interface StorePrice {
  amount: string;       // e.g. "4.99"
  currency: string;     // ISO 4217, e.g. "USD"
  displayPrice: string; // store-formatted, e.g. "$4.99"
}

// Apple: { jws } (a StoreKit 2 signed transaction). Steam: { appTicket }.
export interface IapVerifyRequest {
  jws?: string;
  appTicket?: string;
}
export interface IapVerifyResponse {
  premium: true;
}

// 402 body returned by the HTTP matchmake/create gates (and signalled by a WS
// 402 close) when a native non-premium client requests a paid mode/room.
export interface PremiumRequiredResponse {
  error: "premium_required";
}

// ---------- Admin player panel ----------
export interface AdminPlayerSummary {
  id: number;
  username: string;
  email: string;
  premium: boolean;
  createdAt: number | null; // ms epoch
}
export interface AdminPlayerSearchResponse {
  players: AdminPlayerSummary[];
}
export interface AdminPlayerDetailResponse {
  player: AdminPlayerSummary;
  skins: OwnedSkin[];
  achievements: AchievementProgressEntry[];
}
// ---------- Achievements / skins ----------

// One row per achievement definition, merged with the caller's progress.
export interface AchievementProgressEntry {
  id: string;            // AchievementId
  name: string;
  description: string;
  threshold: number;
  progress: number;      // current counter value, clamped >= 0
  unlockedAt: number | null; // ms epoch, or null while locked
  rewardSkinId: string;  // SkinId
}

// A skin the caller is entitled to equip. "default" is implicit and never
// listed. `source` records how it was obtained; "purchased" rows will be
// written by the platform-store receipt validators (Apple/Google/Steam)
// in a follow-up — the entitlement itself is store-agnostic.
export interface OwnedSkin {
  skinId: string;
  source: "earned" | "purchased" | "granted";
}

export interface AchievementsResponse {
  achievements: AchievementProgressEntry[];
  skins: OwnedSkin[];
}

// Admin: grant a skin to a user out-of-band (promos, support, testing).
export interface AdminGrantSkinRequest {
  username: string;
  skinId: string;
}

export interface LeaderboardRow {
  rank: number;
  username: string;
  kills: number;
  deaths: number;
  kd: number;
  wins: number;
  losses: number;
  gamesPlayed: number;
}
export interface LeaderboardResponse {
  rows: LeaderboardRow[];
}

// ---------- Versions ----------
// Three independent version strings the admin panel maintains:
//   webClient — current Web Client build (shown in lobby)
//   server    — current Server build      (shown in lobby)
//   desktop   — desktop client (stored for the Unity client to consume later)
export interface AppVersions {
  webClient: string;
  server: string;
  desktop: string;
}
export interface VersionsResponse {
  versions: AppVersions;
}
export interface UpdateVersionsRequest {
  webClient?: string;
  server?: string;
  desktop?: string;
}

// ---------- Admin ----------
export interface AdminMapEntry {
  id: string;
  name: string;
  filename: string;
  size: number;
}
export interface AdminMapListResponse {
  maps: AdminMapEntry[];
}
export interface AdminUploadMapRequest {
  filename: string;
  // Base64 of the raw .map file bytes (WinBolo binary format).
  dataBase64: string;
  // Builder sets this when saving over an existing map. The upload-
  // from-disk admin flow leaves it unset to keep its duplicate guard.
  overwrite?: boolean;
}
export interface AdminUploadMapResponse {
  map: AdminMapEntry;
}
// Builder reads the raw bytes of an existing map so it can parse and
// edit it in the browser.
export interface AdminMapRawResponse {
  dataBase64: string;
}

// Random map pool — the set of installed maps eligible to be rolled by
// rooms using RANDOM_MAP_ID (the Skynet room). Lets the admin keep e.g.
// sprawling maps out of the rotation when the player count is low.
export interface AdminRandomMapEntry {
  id: string;
  name: string;
  enabled: boolean; // true = eligible for random rooms
}
export interface AdminRandomMapsResponse {
  maps: AdminRandomMapEntry[];
}
export interface UpdateRandomMapsRequest {
  // Map ids that should be available for random selection. Any installed
  // map omitted here is excluded from the random rotation.
  enabled: string[];
}

// SIEGE map pool — the set of installed maps the random siege map roll draws
// from (mirrors the random/Skynet pool). Solo-challenge rooms always roll a
// random map+theme from this pool.
export interface AdminSiegeMapsResponse {
  maps: AdminRandomMapEntry[];
}
export interface UpdateSiegeMapsRequest {
  enabled: string[];
}

// Public + admin DTO for the "SIEGE enabled on the web client" gate. When OFF
// the web lobby hides the SIEGE mode option, the always-on challenge row, and
// any siege rooms. Used for testing.
export interface SiegeConfigResponse {
  enabled: boolean;
}
export interface UpdateSiegeConfigRequest {
  enabled: boolean;
}

// BATTLE ROYALE map pool — the set of installed maps the random battle-royale
// map roll draws from. Mirrors the SIEGE pool; curated independently in the
// admin panel.
export interface AdminBattleRoyaleMapsResponse {
  maps: AdminRandomMapEntry[];
}
export interface UpdateBattleRoyaleMapsRequest {
  enabled: string[];
}

// Public + admin DTO for the "BATTLE ROYALE enabled on the web client" gate.
// When OFF the web lobby hides the BATTLE ROYALE tile and the matchmaker
// refuses the queue. Used for testing.
export interface BattleRoyaleConfigResponse {
  enabled: boolean;
}
export interface UpdateBattleRoyaleConfigRequest {
  enabled: boolean;
}

// Public + admin DTO for the "TURBO mode enabled on the web client" gate.
// The web lobby reads this to decide whether to surface TURBO World and
// the STYLE dropdown; the admin panel writes it.
export interface TurboConfigResponse {
  enabled: boolean;
}
export interface UpdateTurboConfigRequest {
  enabled: boolean;
}

// Admin-tunable Match Lobby countdown duration (seconds). 0 disables the
// warmup entirely — matchmaking spawns straight into the live match. Range
// 0..60 enforced server-side.
export interface WarmupConfigResponse {
  seconds: number;
}
export interface UpdateWarmupConfigRequest {
  seconds: number;
}

// Admin-tunable minimum Match Lobby time per joiner (seconds). When a player
// connects mid-warmup with less time left than this, the server extends the
// warmup so they're guaranteed at least this many seconds in the lobby. 0
// disables the floor. Range 0..30 enforced server-side.
export interface MinLobbyConfigResponse {
  seconds: number;
}
export interface UpdateMinLobbyConfigRequest {
  seconds: number;
}

// ---------- Matchmaking ----------

export interface MatchmakeRequest {
  mode: MatchmadeMode;
}
export interface MatchmakeResponse {
  gameId: string;
  team: number;
  // ms epoch the warmup countdown ends. Same value the server puts on
  // MatchState.warmupEndsAt — handed back over HTTP so the client can show
  // the countdown before the WebSocket snapshot stream starts.
  warmupEndsAt: number;
  // The mode this room serves (echoed back so the client can keep its
  // local UI state honest after a redirect).
  mode: MatchmadeMode;
}
